import { Button } from "@ariakit/react";
import {
  ListItemContainer,
  ListItemContent,
  ListItemHeading,
  ListItemSummary,
} from "@indietabletop/appkit";
import { useId, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { database } from "../database/database.ts";
import type { Knight } from "../engine/Knight.ts";
import { plural } from "../fmt.ts";
import { useIsReadOnly } from "./ReadOnlyContext.tsx";
import { counter, knightListItem } from "./style.css.ts";

function useTimedExpandState() {
  const [isExpanded, setExpandedAt] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  return {
    ref,
    isExpanded,
    toggleExpanded: () => {
      setExpandedAt((state) => !state);
    },
    handleBlur: (event: React.FocusEvent<HTMLDivElement>) => {
      if (!ref.current?.contains(event.relatedTarget)) {
        setExpandedAt(false);
      }
    },
  };
}

function KnightCounter(props: {
  count: number;
  onIncrement: (num: number) => void;
}) {
  const { count, onIncrement } = props;
  const { isExpanded, ref, toggleExpanded, handleBlur } = useTimedExpandState();
  const counterId = useId();

  return (
    <div
      id={counterId}
      className={counter.root}
      ref={ref}
      onBlur={handleBlur}
      role="group"
      aria-label="Knight type counter"
    >
      <div className={counter.container}>
        <Button
          className={counter.count}
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
          aria-controls={counterId}
          aria-label={`Used ${plural(count, { one: "# time", other: "# times" })}. Toggle increment controls.`}
        >
          {count}x
        </Button>

        <Button
          tabIndex={isExpanded ? 0 : -1}
          style={{ order: -1 }}
          aria-hidden={!isExpanded}
          className={counter.button}
          onClick={() => void onIncrement(-1)}
          aria-label="Decrement"
        >
          -
        </Button>

        <Button
          className={counter.button}
          tabIndex={isExpanded ? 0 : -1}
          aria-hidden={!isExpanded}
          onClick={() => void onIncrement(1)}
          aria-label="Increment"
        >
          +
        </Button>
      </div>
    </div>
  );
}

export function KnightListItem(props: { knight: Knight }) {
  const { knight } = props;
  const [path] = useLocation();
  const isReadOnly = useIsReadOnly();

  const href = `/k/${knight.id}`;
  const current = path.startsWith(href) ? "page" : undefined;

  const handleIncrement = (value: number) => {
    const count = Math.max(knight.count + value, 1);
    const next = knight.clone({ count });
    void database.warbands.updateOne(next.warband.data);
  };

  return (
    <ListItemContainer className={knightListItem.container}>
      <ListItemContent
        render={<Link href={href} aria-current={current} transition />}
        className={knightListItem.content}
      >
        <ListItemHeading className={knightListItem.heading}>
          {knight.name}
        </ListItemHeading>

        <ListItemSummary className={knightListItem.summary}>
          <em>{knight.profile.name}</em>
          {knight.summary}
        </ListItemSummary>
      </ListItemContent>
      {!isReadOnly && (
        <KnightCounter onIncrement={handleIncrement} count={knight.count} />
      )}
    </ListItemContainer>
  );
}
